import { Skeleton } from "../../../../components/ui/common/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import { cn } from "../../../../lib/utils";
import { CompanyRow } from "../types/companyListView.types";
import { COMPANY_TABLE_COLUMNS, companyTableColumnClass } from "./companyTable.columns";
import CompanyTableRow from "./CompanyTableRow";

const COLUMN_COUNT = COMPANY_TABLE_COLUMNS.length + 1; // + actions column
const ROW_SKELETON_COUNT = 5;

interface CompanyTableProps {
  rows: CompanyRow[];
  isLoading: boolean;
  onView: (row: CompanyRow) => void;
  onEdit: (row: CompanyRow) => void;
  onDelete: (row: CompanyRow) => void;
  onAddRecipeIngredients: (row: CompanyRow) => void;
  onAddGenericProcess: (row: CompanyRow) => void;
}

/** Table shell styled after `ProductTable` — same card container, primary-tinted header, skeleton rows while loading. */
const CompanyTable = ({
  rows,
  isLoading,
  onView,
  onEdit,
  onDelete,
  onAddRecipeIngredients,
  onAddGenericProcess
}: CompanyTableProps) => {
  return (
    <div className="tw-relative tw-min-h-[280px] tw-overflow-x-auto tw-rounded-lg tw-border tw-border-border tw-bg-white">
      <Table className="tw-w-full tw-table-fixed [&_td]:tw-px-4 [&_td]:tw-py-3 [&_th]:tw-px-4 [&_th]:tw-py-3">
        <TableHeader>
          <TableRow className="tw-border-b-0 tw-bg-primary/5 hover:tw-bg-primary/5 [&_th]:tw-bg-primary/5 [&_th]:tw-text-primary [&_th]:tw-font-semibold [&_th]:tw-align-middle">
            {COMPANY_TABLE_COLUMNS.map(column => (
              <TableHead
                key={column.key}
                className={cn(companyTableColumnClass[column.key], "tw-text-primary")}
              >
                {column.label}
              </TableHead>
            ))}
            <TableHead className={cn(companyTableColumnClass.actions, "tw-text-right tw-text-primary")}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            [...Array(ROW_SKELETON_COUNT)].map((_, index) => (
              <TableRow key={index} className="tw-bg-white">
                {[...Array(COLUMN_COUNT)].map((__, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    className={
                      COMPANY_TABLE_COLUMNS[cellIndex]
                        ? companyTableColumnClass[COMPANY_TABLE_COLUMNS[cellIndex].key]
                        : undefined
                    }
                  >
                    <Skeleton className="tw-h-4 tw-w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="tw-bg-white hover:tw-bg-white">
              <TableCell
                colSpan={COLUMN_COUNT}
                className="tw-py-12 tw-text-center tw-text-muted-foreground"
              >
                No companies found.
              </TableCell>
            </TableRow>
          ) : (
            rows.map(row => (
              <CompanyTableRow
                key={row._id}
                row={row}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddRecipeIngredients={onAddRecipeIngredients}
                onAddGenericProcess={onAddGenericProcess}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CompanyTable;
